import { HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, throwError } from 'rxjs';
import { UserService, hasResolvedStaffId } from './user.service';
import { PermissionService } from '../database/permission.service';
import { environment } from 'src/environments/environment';
import { AuthErrors } from 'src/app/shared/enums/auth-errors';
// Imported from the individual files, NOT the `shared/utils` barrel. This service is in the eager
// bundle, and the barrel also re-exports `collection-utils`, which pulls in moment — going through
// it costs the initial bundle ~370 kB (measured) and breaks the 2.47 MB budget. `AuthGuard` can use
// the barrel because it is only reachable from lazy routes.
import { isAuthRoute } from 'src/app/shared/utils/auth-route-utils';
import { hasAnyPermission } from 'src/app/shared/utils/permission-utils';
import { TokenService } from './token.service';
import { ApiHelper } from './api-helper';
import { RETURNING_AFTER_SIGN_IN } from './returning-after-sign-in';

@Injectable()
export class AuthService {
  private refreshToken$: Observable<void> | null = null;
  private postLoginInProgress = false;
  /**
   * Where to send the user once the post-login work finishes. Set only when recovering an
   * expired session (see {@link login}), so a fresh sign-in still lands on the homepage.
   */
  private postLoginReturnUrl: string | null = null;
  // Login capture (#1651 Phase A): the auth_time of the last session we beaconed, so a page reload or silent
  // refresh (same session ⇒ same auth_time) does not re-send. The server also dedups; this just avoids the call.
  private lastBeaconedAuthTime: string | null = null;
  private readonly uiAuthenticatedSource = new BehaviorSubject(false);
  public uiAuthenticated: Observable<boolean> = this.uiAuthenticatedSource.asObservable();
  public permissions: number[] = [];
  private userAuthenticated = false;
  private readonly authScopes = ['c2918d13-f931-455d-bcb6-25abdda93b80/.default'];
  private readonly graphScopes = ['https://graph.microsoft.com/.default'];

  constructor(
    private readonly msalService: MsalService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly permissionService: PermissionService,
    private readonly tokenService: TokenService,
    private readonly apiHelper: ApiHelper
  ) {}

  isUserAuthenticated(): boolean {
    this.userAuthenticated = this.msalService.instance.getAllAccounts().length > 0;
    return this.userAuthenticated;
  }

  setUiAuthenticated(value: boolean): void {
    this.userAuthenticated = value;
    this.uiAuthenticatedSource.next(value);
  }

  /**
   * Sign the user in.
   *
   * Pass `returnUrl` when recovering a session whose token has expired, so the user is put back
   * on the page they were already looking at. Without it the user lands on the homepage, which
   * is what a fresh sign-in should do. The recovery path is silent — the user never sees a
   * sign-in screen — so sending them to the homepage looks like the page threw them out.
   */
  login(returnUrl?: string): void {
    this.postLoginReturnUrl = returnUrl ?? null;
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length > 0) {
      const activeAccount = this.msalService.instance.getActiveAccount();

      if (!activeAccount) {
        this.msalService.instance.setActiveAccount(accounts[0]);
      }

      this.msalService.acquireTokenSilent({ scopes: this.authScopes, forceRefresh: true }).subscribe({
        next: (result) => {
          if (result?.accessToken) {
            this.tokenService.setAccessToken(result.accessToken);
          }
          this.postLoginGetUser();
        },
        error: () => {
          console.error('Silent MSAL token acquisition failed; falling back to interactive redirect.');
          this.router.navigate(['redirect']);
          this.msalService.loginRedirect({ scopes: this.authScopes });
        },
      });
      return;
    }

    this.router.navigate(['redirect']);
    this.msalService.loginRedirect({ scopes: this.authScopes });
  }

  postLoginGetUser(): void {
    if (this.postLoginInProgress) {
      return;
    }
    this.postLoginInProgress = true;

    // Read and clear in one go: whichever branch below runs, a return URL is never left behind
    // to be picked up by a later, unrelated sign-in.
    const returnUrl = this.postLoginReturnUrl;
    this.postLoginReturnUrl = null;

    this.userService.getMyInfo().subscribe({
      next: (response) => {
        this.postLoginInProgress = false;
        // hasResolvedStaffId: an answer that names no staff record is a refusal, however it is flagged.
        // Caching it would leave the browser acting as staff 0, who has no access rules (#1873).
        if (response['active'] === true && response['leaveDate'] === null && hasResolvedStaffId(response)) {
          // Login capture (#1651 Phase A): this is the one choke-point where a real, active sign-in is
          // confirmed — reached by both the interactive (postLogin) and silent-recovery (login) paths, and
          // never by a plain cached-token reload or a background heartbeat. Fire the beacon here, once per session.
          this.sendLoginBeacon();
          const user = this.userService.buildUserFromMyInfoResponse(response);
          this.userService.saveUserToLocalDbSubscription(user).then(() => {
            this.userService.updateNotificationsGlobally(user);
            // discardOnFailure: this is the sign-in path, and the only one that goes on to put the
            // user somewhere on the strength of what it just fetched. If it could not be confirmed,
            // the cached row from the last session must not be left behind for the guard to use.
            // It pairs with the auth-error branch below — both say "we could not confirm, so do not
            // proceed" — and the two must not diverge. The app's own routine refresh takes the
            // default and leaves the row alone; see updatePermissions.
            //
            // Ask for the signed-in person's permissions WITHOUT naming a staff id (#1557 round 20, M5).
            // The id-carrying route, api/Permission/{staffId}, is for "view as another member of staff",
            // which passes the impersonated id through it (see AppComponent); the server now resolves the
            // caller and serves another person's set only to someone allowed to view as them (#1668).
            // Sign-in has no reason to use it: the id here is the one MyInfo just resolved for the caller,
            // so the no-id route, which works out who is signed in on the server, returns the same set,
            // and refuses a caller it does not recognise.
            this.updatePermissions(
              null,
              (permissionsLoaded) => {
                this.setUiAuthenticated(true);
                // If a later refresh no longer names a staff record, sign them out the way a refused
                // sign-in is, with the same messages.
                this.userService.startUserInfoSync((refusal) => this.showSignInRefusal(refusal));

                // Never restore a page on permissions we could not confirm (#1670 review, SEC-001).
                // Recovery can put the user back on a gated page before this finishes, authorised by
                // the grants cached from their LAST session; if the refresh then fails, restoring
                // `returnUrl` would keep them there on those stale grants. The stored row has been
                // discarded by now unless that write itself failed, so auth-error is usually where
                // the route guard would send them anyway — and refusing here does not depend on it.
                if (!permissionsLoaded) {
                  this.router.navigate(['auth-error'], { queryParams: { error: AuthErrors.PermissionsMissing } });
                  return;
                }

                this.navigateAfterLogin(returnUrl);
              },
              true
            );
          });
        } else {
          this.showSignInRefusal(response);
        }
      },
      error: (err) => {
        this.postLoginInProgress = false;
        console.error('Failed to load user info after login', err);
        this.router.navigate(['auth-error'], { queryParams: { error: AuthErrors.LostUserDetails } });
      },
    });
  }

  /**
   * Send a refused sign-in to the screen that says why. Used at sign-in and by the background refresh,
   * so a person the API stops recognising mid-session is signed out the same way rather than carried on
   * as "no one".
   */
  private showSignInRefusal(response: NonNullable<unknown>): void {
    // #1557: not every refusal means "your account is not active". When two staff records share
    // this person's email address, sign-in refuses rather than guessing which is theirs — and
    // both records are usually active, so the old shared message told them the opposite of the
    // truth. The API says which case it is; anything it does not name keeps the existing message.
    // 'DuplicateAccounts' is a cross-language contract with the API (VmStaffResponse
    // .SignInProblemDuplicateAccounts). If it is renamed there, this comparison silently stops
    // matching and everyone falls back to "account not active" — the wrong message, and the exact
    // problem this branch exists to fix. TypeScript cannot see it, because the response is untyped.
    // The sign-in contract check (.github/scripts/validate-signin-problem-contract.ps1, run on every
    // push and pull request to master and development) fails when this comparison, the API constant
    // or its field name drift apart, and does the same for 'active' and 'leaveDate' above. It reports
    // the drift; it does not block a merge unless that check is made required. Rename on both sides.
    const error =
      response['signInProblem'] === 'DuplicateAccounts' ? AuthErrors.DuplicateAccounts : AuthErrors.UserNotActive;
    this.router.navigate(['auth-error'], { queryParams: { error } });
  }

  /**
   * Send the user on once the post-login work is done: back to where they were if we recovered
   * an expired session, otherwise to the homepage.
   *
   * Uses `navigateByUrl` rather than `navigate` because the stored value is a whole URL and may
   * carry query parameters — `navigate(['/reports?x=1'])` would escape the `?` into the path.
   */
  private navigateAfterLogin(returnUrl: string | null): void {
    if (!returnUrl || returnUrl === '/' || isAuthRoute(returnUrl)) {
      this.router.navigate(['']);
      return;
    }

    // Recovery runs alongside the app's own first navigation, so by the time permissions have
    // been refreshed the router has usually already put the user on `returnUrl` — activated by
    // the guard reading the permissions cached from their LAST session. Angular ignores a
    // navigation to the URL it is already showing (`onSameUrlNavigation` defaults to 'ignore'),
    // so the `navigateByUrl` below would not re-run that guard. Re-check the page against the
    // permissions just fetched instead: one revoked since the last session would otherwise leave
    // the user sitting on a page they can no longer open.
    if (this.isAlreadyShowing(returnUrl) && !this.activatedRouteAllowedByCurrentPermissions()) {
      this.router.navigate(['']);
      return;
    }

    // Not on the page yet, so its guard does run. Mark the trip so a refusal sends the person home
    // rather than "back", which here has nowhere sensible to go (#1722, see AuthGuard).
    this.router.navigateByUrl(returnUrl, { state: { [RETURNING_AFTER_SIGN_IN]: true } });
  }

  /**
   * True when the router has already activated `url`, so navigating to it again would be ignored.
   *
   * Compares through the router's own serialiser, which is the same rule the router itself uses to
   * decide whether a navigation is going nowhere — so the two agree by construction. This only has
   * to be right in one direction: if it says no when the answer was yes, the navigation below is a
   * real one and the route's guard runs anyway.
   */
  private isAlreadyShowing(url: string): boolean {
    return this.router.parseUrl(url).toString() === this.router.url;
  }

  /**
   * Whether the page the router has already activated is still allowed by the permissions just
   * loaded into {@link permissions}.
   *
   * Reads the same route data as `AuthGuard` and delegates the membership test to the same
   * `hasAnyPermission` helper rather than repeating it. A route that states no `permission`
   * states no requirement, and is left alone. `SitePermissions.All` is `null` rather than
   * absent, so a route asking for it is a stated requirement nobody holds — denied, matching
   * the guard's existing fail-closed reading.
   *
   * The two agree on every route shape in the app, but they are NOT the same rule, so do not
   * treat this as a guaranteed mirror of `AuthGuard`:
   *
   *  - The guard reads only the route it is attached to. This walks every level, so it is the
   *    stricter of the two wherever a parent states a requirement the guard never re-checks.
   *  - The guard denies a route whose `data` has keys but no `permission` — its early return
   *    fires only on data that is entirely empty, so `{ title: 'x' }` reaches
   *    `hasAnyPermission(held, undefined)`, which is false for everyone. This allows it.
   *
   * That second one is the only place this is the more permissive of the two, and it is not
   * reachable: no route in the app has that shape, and one that did would be dead — the guard
   * would refuse it to everybody, so the router could never have activated it, and this is only
   * ever asked about a page already on screen. Both were confirmed against the real guard rather
   * than read off it. Worth re-checking if the guard's early return is ever changed.
   */
  private activatedRouteAllowedByCurrentPermissions(): boolean {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (route) {
      // Angular types route data as `{ [key: string]: any }`, so its shape has to be asserted at
      // this boundary. `AuthGuard` reads the same key with the same meaning.
      const required = route.data['permission'] as number | number[] | undefined;

      if (required !== undefined && !hasAnyPermission(this.permissions, required)) {
        return false;
      }

      route = route.firstChild;
    }

    return true;
  }

  /**
   * Tell the server a real sign-in just completed (login capture, #1651 Phase A). Fire-and-forget: the API
   * records who from the validated token, not this call's body. Deduped on the session's `auth_time` so a
   * reload or silent refresh (same session) does not re-send; a genuine re-login mints a new `auth_time`.
   */
  private sendLoginBeacon(): void {
    const account = this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0];
    // idTokenClaims is an open claims bag at the MSAL boundary; auth_time is a numeric epoch when present.
    const rawAuthTime = account?.idTokenClaims?.['auth_time'];
    const authTime = rawAuthTime != null ? String(rawAuthTime) : null;

    if (authTime !== null && authTime === this.lastBeaconedAuthTime) {
      return;
    }
    this.lastBeaconedAuthTime = authTime;

    this.apiHelper.postWithAccessToken(`${environment.API_URL}v2/session/login`, {}).subscribe({
      error: (err) => console.error('Login beacon failed (non-fatal)', err),
    });
  }

  postLogin(result: AuthenticationResult | null): void {
    if (result?.accessToken) {
      const account = this.msalService.instance.getAllAccounts()[0];
      this.msalService.instance.setActiveAccount(account);
      this.tokenService.setAccessToken(result.accessToken);
    }
    this.postLoginGetUser();
  }

  logout(): void {
    // Fire the logout beacon FIRST — while the access token is still cached. removeAccessToken() below would
    // otherwise leave the beacon tokenless and the server would resolve it to nobody (#1651 Phase C).
    this.sendLogoutBeacon();
    this.tokenService.removeAccessToken();
    this.userService.stopUserInfoSync();
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: environment.logoutRedirectUri,
    });
  }

  /**
   * Tell the server the user is signing out (login capture, #1651 Phase C). Best-effort and honest about it:
   * uses `keepalive` so the request survives the imminent logout navigation, sends ONLY the already-cached
   * token in the Authorization header (never acquires a token at unload), and swallows any error so logout
   * always proceeds. A missing logout event never means "still signed in" — tab close/crash/network can skip it.
   */
  private sendLogoutBeacon(): void {
    const token = this.tokenService.getAccessToken();
    if (!token) {
      return; // no cached token → nothing to attribute; never acquire one at unload
    }

    // Native fetch (not HttpClient) because only fetch supports keepalive, which lets the request outlive the
    // logoutRedirect navigation. Token in the Authorization header only — never the URL/body.
    fetch(`${environment.API_URL}v2/session/logout`, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {
      /* best-effort: logout must proceed regardless of the beacon */
    });
  }

  refreshTokenSilent(): Observable<void> {
    if (this.refreshToken$) {
      return this.refreshToken$;
    }

    const account = this.msalService.instance.getAllAccounts()[0];
    if (!account) {
      return throwError(() => new Error('No MSAL account found'));
    }

    this.msalService.instance.setActiveAccount(account);

    this.refreshToken$ = this.msalService.acquireTokenSilent({ scopes: this.authScopes, forceRefresh: true }).pipe(
      map((result) => {
        this.tokenService.setAccessToken(result.accessToken);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => {
        this.refreshToken$ = null;
      })
    );

    return this.refreshToken$;
  }

  getGraphAccessToken(): Observable<string | null> {
    const activeAccount = this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0];

    if (!activeAccount) {
      return of(null);
    }

    this.msalService.instance.setActiveAccount(activeAccount);

    return this.msalService.acquireTokenSilent({ scopes: this.graphScopes, account: activeAccount }).pipe(
      map((result) => result?.accessToken ?? null),
      catchError((error) => {
        console.error('Graph token acquisition failed', error);
        return of(null);
      })
    );
  }

  addAuthHeader<T>(request: HttpRequest<T>): HttpRequest<T> {
    const token = this.tokenService.getAccessToken();
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    return request;
  }

  /**
   * Refresh the permission set, in memory and in the local store, then hand back.
   *
   * `AuthGuard` authorises real navigations from the STORED row, not from {@link permissions}, so
   * the store write is awaited before the callback runs. Handing back early would let the guard
   * decide against a set that had not landed yet.
   *
   * `discardOnFailure` asks for the stored row to be REMOVED when the refresh does not land
   * (#1670 review, SEC-001 / BR-001 / RACE-001). Only the caller knows whether that is wanted,
   * because it depends on what happens next rather than on why we are refreshing:
   *
   *  - **Restoring a page from a recovered session** — yes. The row holds the permissions from the
   *    previous session, the guard would keep authorising against them, and a permission revoked
   *    since then would stay effective. An absent row is the guard's existing fail-closed state.
   *  - **A routine refresh** (the impersonation switch, and the app's own post-login refresh) — no.
   *    Nothing is being restored, so there is nothing to authorise against a stale set. Removing
   *    the row there would log a working person out on a momentary blip: their next guarded click
   *    would find no row and land on auth-error, and the routine refresh never puts one back.
   *
   * It therefore defaults to `false` — the behaviour every caller had before that review — and the
   * one path that needs fail-closed opts in.
   *
   * The callback is told which happened either way, so a caller that navigates can refuse to send
   * the user somewhere sensitive on a set it could not confirm.
   */
  updatePermissions(
    staffId: number | null,
    callback: (permissionsLoaded: boolean) => void,
    discardOnFailure = false
  ): void {
    this.userService.getPermission(staffId).subscribe({
      next: (permission: number[]) => {
        this.permissions = permission;
        void this.storePermissions(permission, discardOnFailure).then(callback);
      },
      error: (err) => {
        console.error('Failed to load permissions', err);
        this.permissions = [];

        if (!discardOnFailure) {
          callback(false);
          return;
        }

        void this.discardStoredPermissions().then(() => callback(false));
      },
    });
  }

  /**
   * Replace the stored permission row, reporting whether the guard will now read the fresh set.
   *
   * `put` rather than `clear` + `add`: one atomic upsert of the only key this table uses (`1`),
   * with no window where the row is missing and no `add` rejection if it is already there.
   *
   * A failed write is treated exactly like a failed fetch, including whether the old row is
   * discarded — the two are the same situation from the guard's point of view (it is about to read
   * a set we could not confirm), so they must not diverge. See `updatePermissions` for why that is
   * the caller's decision rather than this method's.
   */
  private async storePermissions(permission: number[], discardOnFailure: boolean): Promise<boolean> {
    try {
      await this.permissionService.put(permission, 1);
      return true;
    } catch (err) {
      console.error('Failed to store permissions', err);

      if (discardOnFailure) {
        await this.discardStoredPermissions();
      }

      return false;
    }
  }

  /**
   * Drop the stored permission row so the guard cannot authorise against a set we no longer trust.
   *
   * A failure here is logged and swallowed: there is nothing further this can do about it, and the
   * caller is told the permissions did not load either way, so it still refuses to restore a page.
   */
  private async discardStoredPermissions(): Promise<void> {
    try {
      await this.permissionService.remove(1);
    } catch (err) {
      console.error('Failed to clear stale permissions', err);
    }
  }
}
